import {
  copyTrip,
  DatePick,
  daysBetween,
  Dialog,
  InvalidInput,
  toIso,
  type Plan,
  type TripSummary,
} from '@trippath/shared';
import { addDays, parseISO } from 'date-fns';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { day, problemKey } from '../../lib/format';

interface Props {
  trip: TripSummary;
  /** How many trips branch off it, at any depth: offered along when there are any. */
  branches: number;
  onClose: () => void;
  onCopied: (plan: Plan) => void;
}

/** A trip copied to other dates: its new name and first day, and what comes along (the
 * `TripCopy` mockup). The route and its prices always do; bookings, branches and fellow travellers
 * as chosen; what was spent, owed or filed never does. */
export function CopyDialog({ trip, branches, onClose, onCopied }: Props) {
  const { t, i18n } = useTranslation();
  const nameId = useId();
  const first = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(t('tripCopy.nameOf', { name: trip.name }));
  const [start, setStart] = useState<string | null>(trip.start);
  const [bookings, setBookings] = useState(true);
  const [withBranches, setWithBranches] = useState(true);
  const [travellers, setTravellers] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);

  // How far every day moves, and where the copy then ends.
  const shift = start === null ? 0 : daysBetween(parseISO(trip.start), parseISO(start));

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const missing: Record<string, string> = {};
    if (name.trim() === '') missing.name = t('problem.required');
    if (start === null) missing.start = t('problem.required');
    setErrors(missing);
    setFailed(false);
    if (start === null || Object.keys(missing).length > 0) return;
    copyTrip(trip.id, {
      name,
      start,
      bookings,
      travellers,
      branches: branches > 0 && withBranches,
    }).then(onCopied, (error: unknown) => {
      if (error instanceof InvalidInput) {
        setErrors(
          Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
        );
      } else {
        setFailed(true);
      }
    });
  };

  const option = (
    key: string,
    checked: boolean,
    change: ((value: boolean) => void) | null,
    label: string,
    hint: string,
  ) => (
    <label key={key} className="check copy-option">
      <input
        type="checkbox"
        checked={checked}
        disabled={change === null}
        onChange={(event) => {
          change?.(event.target.checked);
        }}
      />
      <span>
        <span>{label}</span>
        <span className="quiet copy-option-hint">{hint}</span>
      </span>
    </label>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('tripCopy.title', { name: trip.name })}
      initialFocus={first}
    >
      <form className="form" onSubmit={submit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor={nameId} data-required>
            {t('tripCopy.name')}
          </label>
          <input
            ref={first}
            id={nameId}
            className="text-input"
            type="text"
            value={name}
            aria-invalid={errors.name !== undefined}
            aria-describedby={errors.name === undefined ? undefined : `${nameId}-error`}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {errors.name !== undefined && (
            <p className="field-error" id={`${nameId}-error`}>
              {errors.name}
            </p>
          )}
        </div>
        <DatePick label={t('tripCopy.start')} required value={start} onChange={setStart} />
        {errors.start !== undefined && <p className="field-error">{errors.start}</p>}
        {start !== null && (
          <div className="copy-shift">
            <p>
              {t('tripCopy.shift', {
                count: Math.abs(shift),
                sign: shift < 0 ? '−' : '+',
                from: day(start, i18n.language),
                to: day(toIso(addDays(parseISO(trip.end), shift)), i18n.language),
              })}
            </p>
            <p className="quiet">{t('tripCopy.clocks')}</p>
          </div>
        )}
        <fieldset className="copy-holds">
          <legend className="field-label">{t('tripCopy.holds')}</legend>
          {option('route', true, null, t('tripCopy.route'), t('tripCopy.routeHint'))}
          {option('prices', true, null, t('tripCopy.prices'), t('tripCopy.pricesHint'))}
          {option(
            'bookings',
            bookings,
            setBookings,
            t('tripCopy.bookings'),
            t('tripCopy.bookingsHint'),
          )}
          {branches > 0 &&
            option(
              'branches',
              withBranches,
              setWithBranches,
              t('tripCopy.branches', { count: branches }),
              t('tripCopy.branchesHint'),
            )}
          {option(
            'travellers',
            travellers,
            setTravellers,
            t('tripCopy.travellers'),
            t('tripCopy.travellersHint'),
          )}
        </fieldset>
        {failed && (
          <p className="form-failed" role="alert">
            {t('plan.failed')}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('tripCopy.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('tripCopy.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
